// Псевдонимизация пользователя для Hawk.
//
// Наружу НИКОГДА не уезжает ни email, ни имя — только стабильный необратимый
// идентификатор. Он нужен ровно для двух вещей: посчитать, скольких живых людей
// задело ошибкой, и сшить один инцидент между фронтом и бэком — обе стороны
// считают хеш от одного `users.id` и дают одинаковую строку.
//
// Соль зашита константой, а не берётся из env, и это осознанно: на фронте она
// всё равно попадёт в бандл, так что секретности от env не прибавилось бы, зато
// появился бы шанс на рассинхрон web ↔ server (разные соли — один человек
// выглядит как двое). Соль защищает не от владельца userId, а от того, чтобы
// хеши из Hawk совпадали с хешами откуда-либо ещё. Это ОТДЕЛЬНЫЙ хеш от
// серверной анонимизации финансовых/аудит-записей (`ANONYMIZATION_SALT`,
// ФЗ-402) — их смешивать нельзя.
//
// Версия в суффиксе соли — чтобы ротировать одной правкой.

const HAWK_USER_ID_SALT = "studyhub-hawk-v1";

/** Длина итоговой строки в hex-символах (64 бита — коллизии исключены). */
const HAWK_USER_ID_LENGTH = 16;

/**
 * SHA-256 от `salt:userId`, усечённый до 16 hex-символов.
 *
 * Реализация одна на все среды: `globalThis.crypto.subtle` есть и в браузере,
 * и в Node, и в Capacitor WebView. Асинхронность не мешает — отправка событий
 * в Hawk и так fire-and-forget.
 *
 * Возвращает `null`, если Web Crypto недоступен (http:// на LAN-IP в dev):
 * событие уйдёт без привязки к человеку, но не потеряется.
 */
export async function hashUserIdForHawk(userId: string): Promise<string | null> {
	const subtle = globalThis.crypto?.subtle;
	if (!subtle) return null;
	try {
		const data = new TextEncoder().encode(`${HAWK_USER_ID_SALT}:${userId}`);
		const digest = await subtle.digest("SHA-256", data);
		return [...new Uint8Array(digest)]
			.map((b) => b.toString(16).padStart(2, "0"))
			.join("")
			.slice(0, HAWK_USER_ID_LENGTH);
	} catch {
		return null;
	}
}

/**
 * Релиз для события Hawk: `<app>@<sha>`.
 *
 * Проект в Hawk один на монорепозиторий, поэтому релиз обязан быть
 * префиксован приложением — иначе загруженные sourcemap-релизы фронтов и
 * версии бэкенда делят одно пространство имён, и символикация цепляет чужие
 * карты. Тот же префикс обязан использовать загрузчик карт в CI.
 */
export function hawkRelease(app: "server" | "web" | "admin" | "mobile", sha?: string): string {
	return `${app}@${sha || "dev"}`;
}
